import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Kullanıcı kaydı
  const signup = async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Kullanıcı profilini güncelle
      await updateProfile(user, {
        displayName: `${userData.firstName} ${userData.lastName}`
      });

      // Firestore'da kullanıcı belgesi oluştur
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role, // 'teacher' veya 'student'
        createdAt: new Date().toISOString(),
        photoURL: null,
        phoneNumber: null,
        country: 'GB', // İngiltere varsayılan
        language: 'en'
      });

      // Eğer öğretmense, teacher belgesi oluştur
      if (userData.role === 'teacher') {
        await setDoc(doc(db, 'teachers', user.uid), {
          uid: user.uid,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: user.email,
          subjects: [],
          languages: ['en'],
          hourlyRate: 0,
          bio: '',
          education: [],
          experience: [],
          certifications: [],
          availability: {},
          rating: 0,
          reviewCount: 0,
          lessonsCompleted: 0,
          videoIntroUrl: null,
          photoURL: null,
          isActive: true,
          createdAt: new Date().toISOString()
        });
      }

      // ÖNEMLİ: Firestore yazma tamamlandıktan sonra profili yükle
      await loadUserProfile(user.uid);

      return user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  // Kullanıcı girişi
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Kullanıcı çıkışı
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Kullanıcı profilini yükle
  const loadUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
        
        // Eğer öğretmense, teacher profilini de yükle
        if (userDoc.data().role === 'teacher') {
          const teacherDoc = await getDoc(doc(db, 'teachers', uid));
          if (teacherDoc.exists()) {
            setUserProfile(prev => ({
              ...prev,
              teacherProfile: teacherDoc.data()
            }));
          }
        }
      }
    } catch (error) {
      console.error('Load profile error:', error);
    }
  };

  // Auth state değişikliklerini dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};